import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { NavLink, useNavigate } from "react-router-dom";
import CustomPersonIcon from "../../assets/sellerNavbarIcons/ProfileIcon_SellerNavbar.svg";
import arrowDown from "../../assets/sellerNavbarIcons/arrowDown.svg";
import CreateCustomerIcon from "../../assets/sellerNavbarIcons/create_customer.svg";
import CustomersIcon from "../../assets/sellerNavbarIcons/customers.svg";
import CatalogIcon from "../../assets/sellerNavbarIcons/catalog.svg";
import OrdersIcon from "../../assets/sellerNavbarIcons/orders.svg";
import ShipmentIcon from "../../assets/sellerNavbarIcons/shipment.svg";
import InventoryIcon from "../../assets/sellerNavbarIcons/inventory.svg";
import UserManagementIcon from "../../assets/sellerNavbarIcons/userManagement.svg";
import LogoutIcon from "../../assets/sellerNavbarIcons/logout.svg";
import { AuthApiService } from "../../service/authService";
import { useAppDispatch } from "../../redux/hooks";
import { reset } from "../../redux/features/slices/appSlice";

const NavContainer = styled(AppBar)(() => ({
  backgroundColor: "#fff",
  color: "#000",
  boxShadow: "20px",
}));

const NavItem = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",

  gap: "10px",

  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
}));

const IconStyle = {
  width: "20px",
  height: "20px",
  opacity: 1,
};

type SellerNavbarProps = {
  logo?: string;
};

const logoStyle = {
  height: "46px",
  cursor: "pointer",
  margin: "11.5px 55px 11.5px 0px",
};

const SellerNavbar: React.FC<SellerNavbarProps> = ({ logo }) => {
  const orgId = localStorage.getItem("orgId");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const [userName] = useState<string>(localStorage.getItem("Name") || "");
  const NAV_ITEMS = [
    {
      label: "Create Customer",
      to: "create-new-customer",
      icon: CreateCustomerIcon,
    },
    {
      label: "Customers",
      to: "all-customers",
      icon: CustomersIcon,
    },
    { label: "Catalog", to: "products", icon: CatalogIcon },
    { label: "Orders", to: "orders", icon: OrdersIcon },
    {
      label: "Shipment",
      to: "shipment",
      icon: ShipmentIcon,
      style: { width: "15.66px", height: "17px" },
    },
    { label: "Inventory", to: "inventory", icon: InventoryIcon },
    {
      label: userName,
      icon: CustomPersonIcon,
      subMenu: [
        {
          label: "User Management",
          to: "user-management",
          icon: UserManagementIcon,
        },
        {
          label: "Logout",
          action: "logout",
          icon: LogoutIcon,
        },
      ],
    },
  ];

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleMenuItemClick = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const handleLogout = () => {
    AuthApiService.logout();
    dispatch(reset());
    navigate(`/${orgId}`, {
      replace: true,
    });
  };

  const activeLinkStyle = { textDecoration: "underline", color: "#000" };
  const linkStyle = { textDecoration: "none", color: "#000" };

  return (
    <NavContainer position="static">
      <Toolbar>
        {/* Logo Section */}
        <Box>
          <NavLink to="create-new-customer">
            {!logo ? (
              <Skeleton variant="rectangular" sx={logoStyle} />
            ) : (
              <img src={logo} alt="Logo" style={logoStyle} />
            )}
          </NavLink>
        </Box>

        {/* Navigation */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexGrow: 1,
          }}
        >
          {NAV_ITEMS.map(({ label, to, icon, style, subMenu }) =>
            subMenu ? (
              <Box key={label}>
                <IconButton
                  onClick={handleProfileMenuOpen}
                  color="inherit"
                  disableRipple
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <img
                    src={icon}
                    alt={label}
                    style={{ width: "16px", height: "16px" }}
                  />
                  <Typography
                    sx={{
                      paddingX: "12px",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    {label}
                  </Typography>
                  <img src={arrowDown} />
                </IconButton>
                <Menu
                  data-testid="user-menu"
                  anchorEl={anchorEl}
                  open={isMenuOpen}
                  onClose={handleMenuClose}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  sx={{
                    transform: "translateX(30px)",
                    ".css-1toxriw-MuiList-root-MuiMenu-list": {
                      marginTop: "4px",
                      padding: "14px 7px",
                      outline: 0,
                      fontFamily: "Poppins, sans-serif",
                      borderRadius: "10px",
                      border: "1px solid #64748B",
                    },
                    ".MuiPaper-root": {
                      boxShadow: "none",
                    },
                    ".MuiMenuItem-root": {
                      padding: "8px 10px",
                      "&:hover": {
                        borderRadius: "4px",
                      },
                    },
                  }}
                >
                  {subMenu.map(({ label, to, icon, action }, index) => (
                    <React.Fragment key={label}>
                      <MenuItem
                        onClick={() => {
                          if (action === "logout") {
                            handleLogout();
                          } else if (to) {
                            handleMenuItemClick(to);
                          }
                        }}
                      >
                        <img
                          src={icon}
                          alt={label}
                          style={{
                            width: "16px",
                            height: action === "logout" ? "14px" : "16px",
                            paddingRight: "8px",
                          }}
                        />
                        <Typography sx={{ fontSize: "14px" }}>
                          {label}
                        </Typography>
                      </MenuItem>
                      {index < subMenu.length - 1 && (
                        <Divider sx={{ margin: "4px 0" }} />
                      )}
                    </React.Fragment>
                  ))}
                </Menu>
              </Box>
            ) : (
              <NavLink
                key={label}
                to={to}
                style={({ isActive }) =>
                  isActive ? activeLinkStyle : linkStyle
                }
              >
                <NavItem>
                  <img
                    src={icon}
                    alt={label}
                    style={{
                      ...IconStyle,
                      ...style,
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {label}
                  </Typography>
                </NavItem>
              </NavLink>
            )
          )}
        </Box>
      </Toolbar>
    </NavContainer>
  );
};

export default SellerNavbar;
